import { useEffect, useMemo, useState } from "react";
import { Eye, Save, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
} from "@/components/admin/uiKit";
import PromoPopup from "@/components/PromoPopup";

const TEXT_FIELDS = [
  ["headline", "Headline", false],
  ["subhead", "Subhead", true],
  ["code", "Promo code", false],
  ["cta_text", "Primary CTA text", false],
  ["cta_link", "Primary CTA link", false],
  ["close_text", "Dismiss text", false],
  ["disclaimer", "Disclaimer (small print)", true],
];

export default function AdminPromoPopup() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/site-config/promo-popup")
      .then(({ data }) => {
        setConfig(data.config);
        setForm({ ...data.config });
      })
      .catch((err) => toast.error(formatAdminError(err)));
  }, []);

  const dirty = useMemo(() => {
    if (!form || !config) return false;
    return JSON.stringify(form) !== JSON.stringify(config);
  }, [form, config]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        active: !!form.active,
        delay_seconds: parseInt(form.delay_seconds || 0, 10),
        headline: form.headline,
        subhead: form.subhead,
        code: form.code,
        cta_text: form.cta_text,
        cta_link: form.cta_link,
        close_text: form.close_text,
        disclaimer: form.disclaimer,
        hide_when_authenticated: !!form.hide_when_authenticated,
      };
      const { data } = await adminApi.patch(
        "/admin/site-config/promo-popup",
        payload,
      );
      setConfig(data.config);
      setForm({ ...data.config });
      toast.success("Promo popup saved");
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div data-testid="admin-promo-popup">
        <PageHeader overline="Marketing" title="Promo Popup" />
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      </div>
    );
  }

  return (
    <div data-testid="admin-promo-popup">
      <PageHeader
        overline="Marketing"
        title="Promo Popup"
        description="The new-visitor popup on the home page. Toggle active, edit copy, change discount code."
        actions={
          <GhostButton onClick={() => setPreviewing(true)} data-testid="admin-promo-preview">
            <Eye size={14} /> Live preview
          </GhostButton>
        }
      />

      <Panel className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
              Active
            </p>
            <p className="text-sm text-slate-700">
              When off, the popup will not show on any page.
            </p>
          </div>
          <button
            type="button"
            onClick={() => update("active", !form.active)}
            className={`relative h-7 w-12 rounded-full transition-colors ${
              form.active ? "bg-emerald-500" : "bg-slate-300"
            }`}
            data-testid="admin-promo-active-toggle"
            aria-pressed={!!form.active}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                form.active ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEXT_FIELDS.map(([key, label, multi]) => (
            <FormRow key={key} label={label} colspan={multi ? "md:col-span-2" : ""}>
              {multi ? (
                <textarea
                  rows={2}
                  className="form-input"
                  value={form[key] || ""}
                  onChange={(e) => update(key, e.target.value)}
                  data-testid={`admin-promo-${key}`}
                />
              ) : (
                <input
                  className="form-input"
                  value={form[key] || ""}
                  onChange={(e) => update(key, e.target.value)}
                  data-testid={`admin-promo-${key}`}
                />
              )}
            </FormRow>
          ))}
          <FormRow label="Delay (seconds)">
            <input
              type="number"
              min="0"
              max="120"
              className="form-input"
              value={form.delay_seconds ?? 3}
              onChange={(e) => update("delay_seconds", e.target.value)}
              data-testid="admin-promo-delay"
            />
          </FormRow>
          <FormRow label="Hide when signed in">
            <label className="form-input flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.hide_when_authenticated}
                onChange={(e) => update("hide_when_authenticated", e.target.checked)}
              />
              <span className="text-sm text-slate-700">Yes, hide for logged-in customers</span>
            </label>
          </FormRow>
        </div>

        <div className="mt-7 flex items-center gap-2 sticky bottom-0 bg-white pt-2">
          <PrimaryButton onClick={save} disabled={!dirty || saving} data-testid="admin-promo-save">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </PrimaryButton>
          {dirty && (
            <GhostButton onClick={() => setForm({ ...config })}>
              Reset
            </GhostButton>
          )}
        </div>
      </Panel>

      {previewing && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/65 backdrop-blur-sm grid place-items-center px-4"
          role="dialog"
          aria-modal="true"
          data-testid="admin-promo-preview-overlay"
        >
          <button
            type="button"
            aria-label="Close preview"
            onClick={() => setPreviewing(false)}
            className="absolute top-4 right-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/95 text-slate-700 hover:bg-white shadow-md"
            data-testid="admin-promo-preview-close"
          >
            <X size={16} />
          </button>
          <PromoPopup config={form} open={true} onClose={() => setPreviewing(false)} />
        </div>
      )}

      <style>{`
        .form-input {
          width: 100%;
          min-height: 2.75rem;
          border-radius: 0.5rem;
          border: 1px solid #E2E8F0;
          background: #fff;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: #0F172A;
        }
        .form-input:focus {
          outline: none;
          border-color: #38BDF8;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
        }
      `}</style>
    </div>
  );
}

function FormRow({ label, children, colspan = "" }) {
  return (
    <label className={`block ${colspan}`}>
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
